import {
  DEFAULT_ANIMATION_DURATION_SEC,
  type CorankTimelinePoint,
} from '../components/corank-tracker.model'

type CorankSeriesInput =
  | {
      eventCount?: number | null
      windowStart?: string | null
      windowEnd?: string | null
      points?: { coRank: number; timestamp: string }[] | null
    }
  | null
  | undefined

/**
 * Slab samples often map several rows to the same replay `seconds` (e.g. all at `duration`).
 * Keep the **last** sample per bucket so co-rank steps reflect the latest carried value.
 */
/** Collapse only micro float noise; finer than 5ms so ~10 slab keyframes rarely merge away. */
function replaySecondBucket(seconds: number): number {
  return Math.round(seconds * 20000) / 20000
}

export function mergeCorankPointsLastWinsPerSecond(
  points: CorankTimelinePoint[]
): CorankTimelinePoint[] {
  const lastWin = new Map<number, CorankTimelinePoint>()
  points.forEach((point) => {
    const key = replaySecondBucket(point.seconds)
    lastWin.set(key, { ...point, seconds: key })
  })
  return [...lastWin.entries()]
    .sort(([leftSeconds], [rightSeconds]) => leftSeconds - rightSeconds)
    .map(([, point]) => ({ ...point }))
}

/**
 * Collapses consecutive equal co-ranks, then assigns evenly spaced times so the replay uses the
 * full [0, duration] window. Fixes slab-style series that sit at 0 (or flat) for most of real time
 * then jump at the end, or jump once early and never move again.
 *
 * @param terminalCorank When set (live co-rank from GraphQL), the last keyframe uses this value so
 *   slabs that lag the current score still animate to the real display value.
 */
export function redistributeCorankTimelineKeyframesEvenly(
  points: CorankTimelinePoint[],
  durationSec: number,
  terminalCorank?: number
): CorankTimelinePoint[] {
  const durationSeconds = Math.max(1, durationSec)
  if (points.length === 0) {
    const end = terminalCorank ?? 0
    return [
      { seconds: 0, corank: 0 },
      { seconds: durationSeconds, corank: end },
    ]
  }

  const sorted = [...points].sort(
    (left, right) => left.seconds - right.seconds || left.corank - right.corank
  )
  const tailCorank = sorted[sorted.length - 1].corank
  const finalCorank = terminalCorank ?? tailCorank
  const valueChain: CorankTimelinePoint[] = []
  sorted.forEach((point) => {
    const prev = valueChain[valueChain.length - 1]
    if (!prev || prev.corank !== point.corank) {
      valueChain.push({ seconds: point.seconds, corank: point.corank })
    }
  })
  const startCorank = valueChain[0].corank
  if (valueChain.length >= 1) {
    valueChain[valueChain.length - 1] = {
      ...valueChain[valueChain.length - 1],
      corank: finalCorank,
    }
  }
  if (valueChain.length === 1) {
    return [
      { seconds: 0, corank: startCorank },
      { seconds: durationSeconds, corank: finalCorank },
    ]
  }
  return valueChain.map((point, index) => ({
    seconds: (index / (valueChain.length - 1)) * durationSeconds,
    corank: point.corank,
  }))
}

/** Single collapsed sample at/near t=0 keeps its co-rank; otherwise assume missing early history → 0. */
const COLLAPSED_SAMPLE_NEAR_START_SEC = 0.05

/**
 * Turns sparse slab samples into keyframes safe for {@link redistributeCorankTimelineKeyframesEvenly}:
 * anchors t=0, pins t=duration to the live co-rank, nudges duplicate samples at t=0, and if merge
 * collapsed everything to one point near the end, inserts a proper (0, start) → (duration, end) span
 * so the race ramps across the whole replay instead of sitting flat or jumping only at the end.
 */
export function interpolateCorankSlabSamplesToReplayKeyframes(
  samples: CorankTimelinePoint[],
  durationSec: number,
  terminalCorank?: number
): CorankTimelinePoint[] {
  const durationSeconds = Math.max(1, durationSec)
  const valid = samples.filter(
    (p) => Number.isFinite(p.seconds) && Number.isFinite(p.corank)
  )
  if (valid.length === 0) {
    return redistributeCorankTimelineKeyframesEvenly(
      [],
      durationSeconds,
      terminalCorank
    )
  }
  const sorted = [...valid].sort(
    (a, b) => a.seconds - b.seconds || a.corank - b.corank
  )
  const withOrigin =
    sorted[0].seconds > 0 ? [{ seconds: 0, corank: 0 }, ...sorted] : sorted
  const lastIx = withOrigin.length - 1
  const endCorank = terminalCorank ?? withOrigin[lastIx].corank
  let forRedistribute = withOrigin.map((p, i) =>
    i === lastIx ? { ...p, seconds: durationSeconds, corank: endCorank } : p
  )
  if (forRedistribute.length === 1) {
    const only = forRedistribute[0]
    const nearStart = only.seconds < COLLAPSED_SAMPLE_NEAR_START_SEC
    const startCorank = nearStart ? only.corank : 0
    return redistributeCorankTimelineKeyframesEvenly(
      [
        { seconds: 0, corank: startCorank },
        { seconds: durationSeconds, corank: endCorank },
      ],
      durationSeconds,
      endCorank
    )
  }
  if (
    forRedistribute.length >= 2 &&
    forRedistribute[0].seconds === 0 &&
    forRedistribute[0].corank === 0 &&
    forRedistribute[1].seconds === 0
  ) {
    const nudge = Math.min(durationSeconds * 0.06, 1.2)
    forRedistribute = forRedistribute.map((p, iRow) =>
      iRow === 1 ? { ...p, seconds: nudge } : p
    )
  }
  return redistributeCorankTimelineKeyframesEvenly(
    forRedistribute,
    durationSeconds,
    endCorank
  )
}

/**
 * Interpolates slab / API timelines for smooth replay: merge samples that fall in the same time
 * bucket, sync the last sample to live co-rank, then lay out each **distinct** level evenly on
 * [0, durationSec] so the race ramps instead of staying flat (e.g. all zeros) and jumping at the end.
 */
export function interpolateCorankTimelineForReplay(
  points: CorankTimelinePoint[],
  durationSec: number,
  terminalCorank?: number
): CorankTimelinePoint[] {
  const mergedSeconds = mergeCorankPointsLastWinsPerSecond(points)
  return interpolateCorankSlabSamplesToReplayKeyframes(
    mergedSeconds,
    durationSec,
    terminalCorank
  )
}

/**
 * Maps API `corankUpdatedTimelineSeries` (≤10 slab samples: timestamp + coRank) to replay keyframes in [0, durationSec].
 *
 * @param terminalCoRank Live `userCorank` for this row — keeps the animated finish aligned with the number shown beside the runner.
 */
export function corankSeriesToTimelinePoints(
  series: CorankSeriesInput,
  durationSec: number = DEFAULT_ANIMATION_DURATION_SEC,
  terminalCoRank?: number
): CorankTimelinePoint[] | undefined {
  if (!series?.points?.length) {
    return undefined
  }
  if (!series.eventCount) {
    return undefined
  }
  const windowStart = series.windowStart
  const windowEnd = series.windowEnd
  if (!windowStart || !windowEnd) {
    return undefined
  }
  const startMs = new Date(windowStart).getTime()
  const endMs = new Date(windowEnd).getTime()
  const spanMs = endMs - startMs
  const durationSeconds = Math.max(1, durationSec)

  let mapped: CorankTimelinePoint[]
  if (spanMs <= 0) {
    const eventCount = series.points.length
    mapped = series.points.map((point, index) => ({
      seconds:
        eventCount <= 1 ? 0 : (index / (eventCount - 1)) * durationSeconds,
      corank: point.coRank,
    }))
  } else {
    mapped = series.points.map((point) => {
      const timestampMs = new Date(point.timestamp).getTime()
      if (!Number.isFinite(timestampMs)) {
        return { seconds: 0, corank: point.coRank }
      }
      const fraction = (timestampMs - startMs) / spanMs
      const clamped = Math.max(0, Math.min(1, fraction))
      return {
        seconds: clamped * durationSeconds,
        corank: point.coRank,
      }
    })
  }

  const mergedSameSecond = mergeCorankPointsLastWinsPerSecond(mapped)
  return interpolateCorankSlabSamplesToReplayKeyframes(
    mergedSameSecond,
    durationSeconds,
    terminalCoRank
  )
}
